import { useTheme } from '@mui/material';
import { AppCountersContext } from 'app/providers';
import { isNumber } from 'lodash';
import { ChatSessionContext } from 'modules/chat/providers';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import Favicon from 'react-favicon';

const LINE_WIDTH = 1;
const FONT_SIZE = 200;
const CONTEXT_FONT = `bold ${FONT_SIZE / 1.8}px arial`;
const MAX_BADGE_VALUE = 9;

export const AppFavicon: FC = () => {
  const theme = useTheme();

  const { unreadNotificationsCount } = useContext(AppCountersContext);
  const { unreadConversationsCount } = useContext(ChatSessionContext);

  const content = useMemo<number>(() => {
    const value = unreadNotificationsCount + unreadConversationsCount;
    if (!isNumber(value)) {
      return 0;
    }

    return value;
  }, [unreadNotificationsCount, unreadConversationsCount]);

  const renderOverlayHandler = useCallback(
    (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) => {
      if (!content) {
        return;
      }

      const isBadgeTextAvailable = content <= MAX_BADGE_VALUE;
      const canvasProportion = isBadgeTextAvailable ? 2 : 1.7;
      const top = canvas.height / canvasProportion;
      const left = canvas.width / canvasProportion;
      const bottom = canvas.height;
      const right = canvas.width;
      const radius = 48;

      context.fillStyle = theme.palette.error.main;
      context.strokeStyle = theme.palette.error.main;
      context.lineWidth = LINE_WIDTH;

      // Render badge
      context.beginPath();
      context.moveTo(left + radius, top);
      context.quadraticCurveTo(left, top, left, top + radius);
      context.lineTo(left, bottom - radius);
      context.quadraticCurveTo(left, bottom, left + radius, bottom);
      context.lineTo(right - radius, bottom);
      context.quadraticCurveTo(right, bottom, right, bottom - radius);
      context.lineTo(right, top + radius);
      context.quadraticCurveTo(right, top, right - radius, top);
      context.closePath();
      context.fill();

      // Render badge text
      context.font = CONTEXT_FONT;
      context.fillStyle = theme.palette.text.primary;
      context.textAlign = 'left';
      context.textBaseline = 'top';
      context.fillText(isBadgeTextAvailable ? `${content}` : '', 120, 105);
    },
    [content, theme.palette.error.main, theme.palette.text.primary]
  );

  return <Favicon url="favicon.ico" iconSize={200} renderOverlay={renderOverlayHandler} />;
};
